import { createContext, useContext, useReducer, ReactNode, useRef, useCallback, useEffect } from 'react';
import { SimpleGameState, SimpleGameProvider, useSimpleGame } from './SimpleGameContext';
import { ArticleId, SwipeArticle } from '../services/swipe/types';
import { IdResolver } from '../services/swipe/IdResolver';
import { getArticle } from '../services/swipe/SwipeUIFetchService';
import { performanceMonitor } from '../utils/performanceMonitor';

/**
 * Article card data model for swipe UI
 * Extended from Feature Bible's ArticleCardData interface with Step 9 prefixed ID system
 */
/**
 * Anchor data for marking important links
 */
export interface AnchorData {
  title: string;       // Link target article title
  slug: string;        // Wikipedia URL slug (e.g., "Page_Title")
  domAnchorId: string; // Unique DOM identifier for the specific link instance
}

export interface ArticleCardData {
  id: ArticleId;       // Prefixed article identifier (page:123, title:Article, temp:uuid)
  title: string;       // Wikipedia article title
  url: string;         // Wikipedia URL path
  canBack: boolean;    // Whether back button should be shown
  content?: string;    // HTML content for display
  extract?: string;    // Plain text summary
  loading?: boolean;   // Whether content is currently being fetched
  cssUrl?: string;     // Wikipedia CSS URL for faithful rendering
  baseStyles?: string; // Inline critical Wikipedia styles
  anchors?: AnchorData[]; // Anchored links (max 3 per card)
}

/**
 * User action tracking for complete game history
 */
export interface UserAction {
  type: 'navigate' | 'back' | 'start' | 'goal';
  article: string;
  timestamp: number;
  stepNumber: number; // Actual step count (only increments for forward navigation)
}

/**
 * Enhanced game state for swipe UI extending SimpleGameState
 * Maps SimpleGameState.path[] to cards[] following Feature Bible spec
 */
export interface SwipeGameState extends SimpleGameState {
  cards: ArticleCardData[];  // Card stack (maps from path[])
  active: number;            // Currently active card index (for Embla)
  goalCard: ArticleCardData | null; // Goal card (always last in stack)
  actionHistory: UserAction[]; // Complete history of user actions
  totalSteps: number;        // Actual step count (increments only for forward moves)
}

/**
 * Swipe-specific actions extending the base game actions
 */
type SwipeGameAction =
  | { type: 'ADD_CARD'; payload: { card: ArticleCardData } }
  | { type: 'BACK_TO_CARD'; payload: { cardId: ArticleId } }
  | { type: 'SET_ACTIVE_CARD'; payload: { index: number } }
  | { type: 'START_SWIPE_GAME'; payload: { startArticle: string; goalArticle: string; minSteps?: number } }
  | { type: 'NAVIGATE_SWIPE'; payload: { article: string; steps: number } }
  | { type: 'RESET_SWIPE_GAME' }
  | { type: 'UPDATE_CARD_CONTENT'; payload: { cardId: ArticleId; content: string; extract?: string | undefined; cssUrl?: string; baseStyles?: string } }
  | { type: 'SET_CARD_LOADING'; payload: { cardId: ArticleId; loading: boolean } }
  | { type: 'ADD_ANCHOR'; payload: { cardId: ArticleId; anchor: AnchorData } }
  | { type: 'REMOVE_ANCHOR'; payload: { cardId: ArticleId; domAnchorId: string } }
  | { type: 'GAME_WON' };

/**
 * Maps SimpleGameState to SwipeGameState by converting path[] to cards[]
 */
function mapSimpleStateToSwipeState(simpleState: SimpleGameState, goalArticle?: string): SwipeGameState {
  // Convert path to cards using new prefixed ID system
  const cards: ArticleCardData[] = simpleState.path.map((title: string, index: number) => {
    const articleId = IdResolver.createTitleId(title);
    return {
      id: articleId,
      title: title,
      url: IdResolver.toUrlPath(articleId),
      canBack: index > 0 && simpleState.backButtonsLeft > 0 // Can go back if not first card and back buttons available
    };
  });

  // Add goal card at the end if specified
  let goalCard: ArticleCardData | null = null;
  if (goalArticle && goalArticle !== simpleState.currentArticle) {
    const goalId = IdResolver.createTitleId(goalArticle);
    goalCard = {
      id: goalId,
      title: goalArticle,
      url: IdResolver.toUrlPath(goalId),
      canBack: false
    };
    cards.push(goalCard);
  }

  return {
    ...simpleState,
    cards,
    active: Math.max(0, cards.length - (goalCard ? 2 : 1)), // Active is current article index
    goalCard,
    actionHistory: [], // Initialize empty, will be populated by actions
    totalSteps: simpleState.steps // Use simple state steps as starting point
  };
}

/**
 * Reducer for swipe game state management
 * Follows Feature Bible's card insertion and back button logic
 */
function swipeGameReducer(state: SwipeGameState, action: SwipeGameAction): SwipeGameState {
  switch (action.type) {
    case 'ADD_CARD':
      // Insert new card before goal card (splice-before-Goal pattern from Feature Bible)
      const newCards = [...state.cards];
      const goalIndex = state.goalCard ? newCards.length - 1 : newCards.length;
      
      // Insert new card before goal
      newCards.splice(goalIndex, 0, {
        ...action.payload.card,
        canBack: true
      });
      
      return {
        ...state,
        cards: newCards,
        active: goalIndex // Focus on newly inserted card
      };

    case 'BACK_TO_CARD':
      // Back button logic from Feature Bible - prunes cards to the right
      console.log(`🔍 [DEBUG] BACK_TO_CARD reducer called for cardId: ${action.payload.cardId}`);
      console.log(`🔍 [DEBUG] Cards before back operation:`, state.cards.map(c => `${c.id}: ${c.title} (content: ${c.content ? 'exists' : 'null'})`));
      
      const cardIndex = state.cards.findIndex(c => c.id === action.payload.cardId);
      console.log(`🔍 [DEBUG] Target card index: ${cardIndex}`);
      
      if (cardIndex === -1) {
        console.log(`🔍 [DEBUG] Card not found, returning unchanged state`);
        return state;
      }
      
      // Keep cards up to and including the target card, plus goal card
      const prunedCards = [
        ...state.cards.slice(0, cardIndex + 1).map(c => ({ ...c, canBack: false })),
        ...(state.goalCard ? [state.goalCard] : [])
      ];
      
      console.log(`🔍 [DEBUG] Cards after back operation:`, prunedCards.map(c => `${c.id}: ${c.title} (content: ${c.content ? 'exists' : 'null'})`));
      console.log(`🔍 [DEBUG] Removed ${state.cards.length - prunedCards.length + (state.goalCard ? 1 : 0)} cards`);
      
      const targetCard = state.cards[cardIndex];
      const backAction: UserAction = {
        type: 'back',
        article: targetCard.title,
        timestamp: Date.now(),
        stepNumber: state.totalSteps + 1 // Back DOES count as a step in the actual path
      };
      
      // Only update swipe-specific state - let mapping function handle SimpleGameState properties
      return {
        ...state,
        cards: prunedCards,
        active: cardIndex,
        actionHistory: [...state.actionHistory, backAction],
        totalSteps: state.totalSteps + 1 // Back button DOES count as a step
      };

    case 'SET_ACTIVE_CARD':
      // Update active card index for Embla carousel sync
      return {
        ...state,
        active: Math.max(0, Math.min(action.payload.index, state.cards.length - 1))
      };

    case 'START_SWIPE_GAME':
      // Initialize swipe game with start area card, start and goal articles
      // Start area card is at index 0, viewport starts there
      const startAreaCard: ArticleCardData = {
        id: 'start-area' as ArticleId,
        title: '__START_AREA__', // Special marker for rendering
        url: '',
        canBack: false,
        content: '', // No content needed
        loading: false
      };

      const startId = IdResolver.createTitleId(action.payload.startArticle);
      const startCard: ArticleCardData = {
        id: startId,
        title: action.payload.startArticle,
        url: IdResolver.toUrlPath(startId),
        canBack: false,
        content: null, // Explicitly set to null to trigger content loading
        loading: false // Initialize loading state
      };

      const goalId = IdResolver.createTitleId(action.payload.goalArticle);
      const newGoalCard: ArticleCardData = {
        id: goalId,
        title: action.payload.goalArticle,
        url: IdResolver.toUrlPath(goalId),
        canBack: false,
        content: null, // Explicitly set to null to trigger content loading
        loading: false // Initialize loading state
      };

      const startAction: UserAction = {
        type: 'start',
        article: action.payload.startArticle,
        timestamp: Date.now(),
        stepNumber: 0
      };

      return {
        startArticle: action.payload.startArticle,
        goalArticle: action.payload.goalArticle,
        currentArticle: action.payload.startArticle,
        path: [action.payload.startArticle],
        steps: 0,
        backButtonsLeft: 1,
        backButtonUsed: false,
        isGameActive: true,
        startTime: Date.now(),
        minSteps: action.payload.minSteps,
        cards: [startAreaCard, startCard, newGoalCard],
        active: 0, // Start at start area card, viewport shows this initially
        goalCard: newGoalCard,
        actionHistory: [startAction],
        totalSteps: 0
      };

    case 'NAVIGATE_SWIPE':
      // Handle navigation in swipe mode
      const navTitle = action.payload.article;
      console.log(`🔍 [DEBUG] NAVIGATE_SWIPE reducer called for article: ${navTitle}`);
      console.log(`🔍 [DEBUG] Current cards before navigation:`, state.cards.map(c => `${c.id}: ${c.title} (content: ${c.content ? 'exists' : 'null'})`));
      
      // Check if navigating to the goal article
      if (state.goalCard && navTitle === state.goalCard.title) {
        console.log(`🔍 [DEBUG] Navigating to goal article, not creating new card`);

        const goalAction: UserAction = {
          type: 'goal',
          article: navTitle,
          timestamp: Date.now(),
          stepNumber: state.totalSteps + 1 // Goal click counts as a step
        };

        // Navigating to goal - make goal card active instead of creating new card
        const goalIndex = state.cards.findIndex(card => card.id === state.goalCard!.id);

        return {
          ...state,
          currentArticle: navTitle,
          path: [...state.path, navTitle],
          steps: action.payload.steps,
          active: goalIndex >= 0 ? goalIndex : state.cards.length - 1, // Focus on goal card
          actionHistory: [...state.actionHistory, goalAction],
          totalSteps: state.totalSteps + 1 // Goal navigation counts as a step
        };
      }
      
      // Check if article already exists in the path (allow duplicate cards)
      // Create unique ID for duplicate articles by adding a suffix
      const existingArticles = state.cards.filter(card => card.title === navTitle);
      const baseId = IdResolver.createTitleId(navTitle);
      const navId = existingArticles.length > 0 
        ? `${baseId}#${existingArticles.length + 1}` as ArticleId
        : baseId;
        
      console.log(`🔍 [DEBUG] Creating new card with ID: ${navId} (base: ${baseId}, existing count: ${existingArticles.length})`);
        
      const navCard: ArticleCardData = {
        id: navId,
        title: navTitle,
        url: IdResolver.toUrlPath(baseId), // Use base ID for URL
        canBack: true,
        content: null, // Explicitly set to null to trigger content loading
        loading: false // Initialize loading state
      };

      // Insert before goal card (correct order: Start → Steps → Goal)
      const currentCards = [...state.cards];
      const insertIndex = state.goalCard ?
        currentCards.length - 1 : // Before goal card
        currentCards.length;      // At end if no goal
      currentCards.splice(insertIndex, 0, navCard);

      console.log(`🔍 [DEBUG] Cards after navigation insert:`, currentCards.map(c => `${c.id}: ${c.title} (content: ${c.content ? 'exists' : 'null'})`));
      console.log(`🔍 [DEBUG] New card inserted at index: ${insertIndex}, setting as active`);
      console.log(`🔍 [DEBUG] NAVIGATE_SWIPE returning state with active: ${insertIndex}`);

      const navAction: UserAction = {
        type: 'navigate',
        article: navTitle,
        timestamp: Date.now(),
        stepNumber: state.totalSteps + 1
      };

      const newState = {
        ...state,
        currentArticle: navTitle,
        path: [...state.path, navTitle],
        steps: action.payload.steps,
        cards: currentCards,
        active: insertIndex, // Focus on newly inserted card
        actionHistory: [...state.actionHistory, navAction],
        totalSteps: state.totalSteps + 1
      };

      console.log(`🔍 [DEBUG] NAVIGATE_SWIPE new state.active: ${newState.active}, cards.length: ${newState.cards.length}`);

      return newState;

    case 'RESET_SWIPE_GAME':
      return {
        startArticle: '',
        goalArticle: '',
        currentArticle: '',
        path: [],
        steps: 0,
        backButtonsLeft: 1,
        backButtonUsed: false,
        isGameActive: false,
        startTime: 0,
        minSteps: undefined,
        cards: [],
        active: 0,
        goalCard: null,
        actionHistory: [],
        totalSteps: 0
      };

    case 'UPDATE_CARD_CONTENT':
      // Update card with fetched content
      console.log(`🔍 [DEBUG] UPDATE_CARD_CONTENT reducer called for cardId: ${action.payload.cardId}`);
      console.log(`🔍 [DEBUG] Content payload:`, {
        hasContent: !!action.payload.content,
        contentLength: action.payload.content?.length || 0,
        hasExtract: !!action.payload.extract,
        extractLength: action.payload.extract?.length || 0
      });
      console.log(`🔍 [DEBUG] Cards before update:`, state.cards.map(c => `${c.id}: ${c.title} (content: ${c.content ? 'exists' : 'null'})`));
      
      const updatedCards = state.cards.map(card => {
        const isMatch = IdResolver.equals(card.id, action.payload.cardId);
        console.log(`🔍 [DEBUG] Checking card ${card.id} vs ${action.payload.cardId}: ${isMatch ? 'MATCH' : 'no match'}`);

        return isMatch
          ? {
              ...card,
              content: action.payload.content,
              extract: action.payload.extract,
              cssUrl: action.payload.cssUrl,
              baseStyles: action.payload.baseStyles,
              loading: false
            } as ArticleCardData
          : card;
      });

      const updatedGoalCard = state.goalCard && IdResolver.equals(state.goalCard.id, action.payload.cardId)
        ? {
            ...state.goalCard,
            content: action.payload.content,
            extract: action.payload.extract,
            cssUrl: action.payload.cssUrl,
            baseStyles: action.payload.baseStyles,
            loading: false
          } as ArticleCardData
        : state.goalCard;
      
      console.log(`🔍 [DEBUG] Cards after update:`, updatedCards.map(c => `${c.id}: ${c.title} (content: ${c.content ? 'exists' : 'null'})`));
      if (updatedGoalCard && state.goalCard && IdResolver.equals(state.goalCard.id, action.payload.cardId)) {
        console.log(`🔍 [DEBUG] Goal card updated: ${updatedGoalCard.id} (content: ${updatedGoalCard.content ? 'exists' : 'null'})`);
      }
      
      return {
        ...state,
        cards: updatedCards,
        goalCard: updatedGoalCard
      };

    case 'SET_CARD_LOADING':
      // Set loading state for a card
      return {
        ...state,
        cards: state.cards.map(card =>
          IdResolver.equals(card.id, action.payload.cardId)
            ? { ...card, loading: action.payload.loading }
            : card
        ),
        goalCard: state.goalCard && IdResolver.equals(state.goalCard.id, action.payload.cardId)
          ? { ...state.goalCard, loading: action.payload.loading }
          : state.goalCard
      };

    case 'GAME_WON':
      // When game is won, set all cards' canBack to false to hide bookmarks
      return {
        ...state,
        cards: state.cards.map(card => ({ ...card, canBack: false })),
        goalCard: state.goalCard ? { ...state.goalCard, canBack: false } : null
      };

    case 'ADD_ANCHOR':
      // Add anchor to specified card (max 3 per card)
      return {
        ...state,
        cards: state.cards.map(card => {
          if (IdResolver.equals(card.id, action.payload.cardId)) {
            const currentAnchors = card.anchors || [];
            // Prevent duplicates and enforce max 3
            if (currentAnchors.length >= 3) {
              console.warn(`⚠️ [SwipeGameContext] Max anchors (3) reached for card ${card.id}`);
              return card;
            }
            // Check if anchor already exists (by domAnchorId)
            if (currentAnchors.some(a => a.domAnchorId === action.payload.anchor.domAnchorId)) {
              console.warn(`⚠️ [SwipeGameContext] Anchor already exists: ${action.payload.anchor.domAnchorId}`);
              return card;
            }
            return {
              ...card,
              anchors: [...currentAnchors, action.payload.anchor]
            };
          }
          return card;
        }),
        goalCard: state.goalCard && IdResolver.equals(state.goalCard.id, action.payload.cardId)
          ? (() => {
              const currentAnchors = state.goalCard!.anchors || [];
              if (currentAnchors.length >= 3) return state.goalCard;
              if (currentAnchors.some(a => a.domAnchorId === action.payload.anchor.domAnchorId)) return state.goalCard;
              return {
                ...state.goalCard!,
                anchors: [...currentAnchors, action.payload.anchor]
              };
            })()
          : state.goalCard
      };

    case 'REMOVE_ANCHOR':
      // Remove anchor from specified card by domAnchorId
      return {
        ...state,
        cards: state.cards.map(card => {
          if (IdResolver.equals(card.id, action.payload.cardId)) {
            const currentAnchors = card.anchors || [];
            return {
              ...card,
              anchors: currentAnchors.filter(a => a.domAnchorId !== action.payload.domAnchorId)
            };
          }
          return card;
        }),
        goalCard: state.goalCard && IdResolver.equals(state.goalCard.id, action.payload.cardId)
          ? {
              ...state.goalCard,
              anchors: (state.goalCard.anchors || []).filter(a => a.domAnchorId !== action.payload.domAnchorId)
            }
          : state.goalCard
      };

    default:
      return state;
  }
}

export interface SwipeGameContextType {
  gameState: SwipeGameState;
  dispatch: React.Dispatch<SwipeGameAction>;
  simpleGameState: SimpleGameState;
  simpleDispatch: React.Dispatch<any>;
  fetchArticleContent: (cardId: ArticleId) => Promise<void>;
}

const SwipeGameContext = createContext<SwipeGameContextType | undefined>(undefined);

/**
 * SwipeGameProvider wraps SimpleGameProvider and extends it with swipe functionality
 * Maintains backward compatibility with existing SimpleGameContext usage
 */
export function SwipeGameProvider({ children }: { children: ReactNode }) {
  return (
    <SimpleGameProvider>
      <SwipeGameProviderInner>
        {children}
      </SwipeGameProviderInner>
    </SimpleGameProvider>
  );
}

function SwipeGameProviderInner({ children }: { children: ReactNode }) {
  const { gameState: simpleGameState, dispatch: simpleDispatch } = useSimpleGame();
  
  // Initialize swipe state from simple state
  const initialSwipeState: SwipeGameState = mapSimpleStateToSwipeState(simpleGameState, simpleGameState.goalArticle);
  const [gameState, rawDispatch] = useReducer(swipeGameReducer, initialSwipeState);
  
  // Debounced dispatch to prevent rapid conflicting updates
  const pendingUpdatesRef = useRef<SwipeGameAction[]>([]);
  const dispatchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const dispatch = useCallback((action: SwipeGameAction) => {
    // Immediate dispatch for critical actions
    if (action.type === 'START_SWIPE_GAME' || action.type === 'RESET_SWIPE_GAME') {
      performanceMonitor.measure(`dispatch-${action.type}`, () => {
        rawDispatch(action);
      }, { action: action.type });
      return;
    }
    
    // For rapid actions, batch them
    pendingUpdatesRef.current.push(action);
    
    if (dispatchTimeoutRef.current) {
      clearTimeout(dispatchTimeoutRef.current);
    }
    
    dispatchTimeoutRef.current = setTimeout(() => {
      const updates = [...pendingUpdatesRef.current];
      pendingUpdatesRef.current = [];
      
      // Process batched updates with performance monitoring
      performanceMonitor.measure('batch-dispatch', () => {
        updates.forEach(update => rawDispatch(update));
      }, { 
        batchSize: updates.length, 
        actions: updates.map(u => u.type) 
      });
      
      dispatchTimeoutRef.current = null;
    }, 16); // ~60fps batching
  }, []);

  // Cleanup pending dispatches on unmount
  useEffect(() => {
    return () => {
      if (dispatchTimeoutRef.current) {
        clearTimeout(dispatchTimeoutRef.current);
      }
    };
  }, []);

  // Function to fetch article content using Step 9 service
  const fetchArticleContent = async (cardId: ArticleId): Promise<void> => {
    console.log(`🔍 [DEBUG] fetchArticleContent called with cardId: ${cardId}`);
    console.log(`🔍 [DEBUG] Current cards in gameState:`, gameState.cards.map(c => `${c.id}: ${c.title} (content: ${c.content ? 'exists' : 'null'})`));
    
    try {
      // Set loading state
      console.log(`🔍 [DEBUG] Setting loading state for cardId: ${cardId}`);
      dispatch({ 
        type: 'SET_CARD_LOADING', 
        payload: { cardId, loading: true } 
      });

      // For duplicate articles, extract base ID by removing suffix
      const baseCardId = cardId.includes('#') 
        ? cardId.split('#')[0] as ArticleId
        : cardId;
      console.log(`🔍 [DEBUG] Base card ID: ${baseCardId} (original: ${cardId})`);

      // Check if we already have content for this article title in another card
      const targetCard = gameState.cards.find(card => IdResolver.equals(card.id, cardId));
      console.log(`🔍 [DEBUG] Target card found:`, targetCard ? `${targetCard.id}: ${targetCard.title}` : 'NOT FOUND');
      
      if (targetCard) {
        const articleTitle = targetCard.title;
        console.log(`🔍 [DEBUG] Searching for existing content for title: ${articleTitle}`);
        
        const existingCardWithContent = gameState.cards.find(card => 
          card.title === articleTitle && card.content && card.content.trim() !== ''
        );
        console.log(`🔍 [DEBUG] Existing card with content:`, existingCardWithContent ? `${existingCardWithContent.id}: ${existingCardWithContent.title}` : 'NOT FOUND');
        
        if (existingCardWithContent) {
          console.log(`📋 [SwipeGameContext] Copying content for duplicate article: ${articleTitle}`);
          console.log(`🔍 [DEBUG] Dispatching UPDATE_CARD_CONTENT with copied content`);
          // Copy content from existing card
          dispatch({
            type: 'UPDATE_CARD_CONTENT',
            payload: {
              cardId,
              content: existingCardWithContent.content,
              extract: existingCardWithContent.extract,
              cssUrl: existingCardWithContent.cssUrl,
              baseStyles: existingCardWithContent.baseStyles
            }
          });
          console.log(`🔍 [DEBUG] Content copy dispatch completed for cardId: ${cardId}`);
          return;
        }
      }

      // Fetch article using Step 9 service with base ID
      console.log(`🌐 [SwipeGameContext] Fetching fresh content for: ${baseCardId}`);
      console.log(`🔍 [DEBUG] Calling getArticle with baseCardId: ${baseCardId}`);
      
      const article: SwipeArticle = await getArticle(baseCardId, {
        includeContent: true,
        includeLinks: false, // Links not needed for display
        includeMetadata: false,
        priority: 'normal',
        source: 'swipe-context',
      });

      console.log(`🔍 [DEBUG] getArticle response:`, {
        title: article.title,
        hasContent: !!article.content,
        contentLength: article.content?.length || 0,
        hasExtract: !!article.extract,
        extractLength: article.extract?.length || 0
      });

      // Update card with content
      console.log(`🔍 [DEBUG] Dispatching UPDATE_CARD_CONTENT with fresh content for cardId: ${cardId}`);
      dispatch({
        type: 'UPDATE_CARD_CONTENT',
        payload: {
          cardId,
          content: article.content || '',
          extract: article.extract || undefined,
          cssUrl: article.cssUrl,
          baseStyles: article.baseStyles
        }
      });
      console.log(`🔍 [DEBUG] Fresh content dispatch completed for cardId: ${cardId}`);

    } catch (error) {
      console.error(`🚨 [DEBUG] Failed to fetch content for article ${cardId}:`, error);
      console.error(`🚨 [DEBUG] Error details:`, {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      // Clear loading state on error
      console.log(`🔍 [DEBUG] Clearing loading state due to error for cardId: ${cardId}`);
      dispatch({ 
        type: 'SET_CARD_LOADING', 
        payload: { cardId, loading: false } 
      });
    }
  };

  return (
    <SwipeGameContext.Provider value={{ 
      gameState, 
      dispatch, 
      simpleGameState, 
      simpleDispatch,
      fetchArticleContent
    }}>
      {children}
    </SwipeGameContext.Provider>
  );
}

/**
 * Hook for accessing swipe game context
 * Provides both swipe-specific state and underlying simple game state
 */
export function useSwipeGame() {
  const context = useContext(SwipeGameContext);
  if (context === undefined) {
    throw new Error('useSwipeGame must be used within a SwipeGameProvider');
  }
  return context;
}

/**
 * Utility function to create ArticleCardData from article title
 * Uses Step 9 prefixed ID system with title: prefix
 */
export function createArticleCard(title: string, canBack: boolean = true): ArticleCardData {
  const articleId = IdResolver.createTitleId(title);
  return {
    id: articleId,
    title: title,
    url: IdResolver.toUrlPath(articleId),
    canBack: canBack
  };
}

/**
 * Utility function to create ArticleCardData from prefixed ArticleId
 * Allows creating cards with any supported ID format (page:, title:, temp:)
 */
export function createArticleCardFromId(id: ArticleId, canBack: boolean = true): ArticleCardData {
  const parsed = IdResolver.parse(id);
  
  // Extract title based on ID type
  let title: string;
  if (parsed.type === 'title') {
    title = parsed.value;
  } else if (parsed.type === 'temp') {
    title = `Temp Card: ${parsed.value}`;
  } else {
    // For page IDs, we would need to resolve to title, but for now use ID as placeholder
    title = `Page ${parsed.value}`;
  }
  
  return {
    id: id,
    title: title,
    url: IdResolver.toUrlPath(id, parsed.type === 'page' ? title : undefined),
    canBack: canBack
  };
}